import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-types-papi';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import FeedThumb from '@wix/da-shared-react/pkg/DeviationViews/Feed';
import { isDeviationTextEditable } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import MeasuredViewerContainerSizeContextProvider from '@wix/da-shared-react/pkg/DaEditorWrapper/ViewerContainerSizeContext/MeasuredViewerContainerSizeContextProvider';
import { MeasuredCookieType } from '@wix/da-shared-react/pkg/Measured/redux/types';
import LoadableDuperbrowseContext from '../../../../../contexts/LoadableDuperbrowseContext';
import FeedPagination from './FeedPagination';

import s from './PostsFeed.scss';

export interface Props {
  streamId: string;
  className?: string;
  isOwner: boolean;

  items: PapiDeviation[];

  isItemFeatured: (item: PapiDeviation) => boolean;
  editItem: (item: PapiDeviation) => void;
  featureItem: (item: PapiDeviation) => void;
  unfeatureItem: (item: PapiDeviation) => void;
  pinItem: (item: PapiDeviation) => void;
  unpinItem: () => void;
}

const PostsFeed: React.FC<Props> = ({
  isOwner,
  items,
  streamId,
  className,
  pinItem,
  unpinItem,
  featureItem,
  unfeatureItem,
  editItem,
  isItemFeatured,
}) => {
  const { t } = useTranslation();
  return (
    <ErrorBoundary>
      <LoadableDuperbrowseContext
        streamId={streamId}
        parent={{ title: t('duperbrowse.backButton.profile.posts') }}
      >
        <MeasuredViewerContainerSizeContextProvider
          cookieType={MeasuredCookieType.BROWSE_POST_FEED}
          cookieDim={'width'}
          className={classnames(s['items'], className)}
          innerPadding={16}
          listenOnWindow
        >
          {items.map((item, index) => {
            const isFeatured = isItemFeatured(item);
            const isPinned = !!item['isPinned'];
            return (
              <FeedThumb
                key={`${index}-${item.typeId}-${item.deviationId}-${item['isPinned']}`}
                withWatch
                withComments
                deviation={item}
                className={s['feed-item']}
                extraActions={
                  !isOwner
                    ? undefined
                    : [
                        isPinned && {
                          label: t('widgets.feed.unpin'),
                          onClick: () => unpinItem(),
                        },
                        !isPinned && {
                          label: t('widgets.feed.pin'),
                          onClick: () => pinItem(item),
                        },
                        !isFeatured && {
                          label: t('widgets.feed.feature'),
                          onClick: () => featureItem(item),
                        },
                        isFeatured && {
                          label: t('widgets.feed.unfeature'),
                          onClick: () => unfeatureItem(item),
                        },
                        isDeviationTextEditable(item) && {
                          label: t('widgets.feed.edit'),
                          onClick: () => editItem(item),
                        },
                      ]
                }
              />
            );
          })}
        </MeasuredViewerContainerSizeContextProvider>
        <FeedPagination streamId={streamId} />
      </LoadableDuperbrowseContext>
    </ErrorBoundary>
  );
};

PostsFeed.displayName = 'PostsFeed';
export default PostsFeed;
